import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import { ArrowLeft, Download, FileText, Send, Star, EyeOff, Upload } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  publiee: "bg-green-50 text-green-700",
  brouillon: "bg-amber-50 text-amber-700",
  retiree: "bg-slate-100 text-slate-500",
};
const STATUS_LABEL = { publiee: "Publiée", brouillon: "Brouillon", retiree: "Retirée" };

export default function Brochures() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [testEmail, setTestEmail] = useState("");

  const allowed = user && user.role === "super_admin";

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
    else if (!authLoading && user && user.role !== "super_admin") navigate("/prospection", { replace: true });
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    const res = await api.get("/brochures");
    setItems(res.data.items || []);
  }, []);

  useEffect(() => {
    if (allowed) load().catch((e) => setError(formatApiError(e)));
  }, [allowed, load]);

  if (!user || !allowed) return null;

  const act = async (key, fn, okMsg) => {
    setError(""); setNotice(""); setBusy(key);
    try {
      await fn();
      setNotice(okMsg);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    setBusy("");
  };

  const generate = (lang) => act(`gen-${lang}`, () => api.post("/brochures/generate", { language: lang }),
    `Brochure ${lang.toUpperCase()} générée (brouillon).`);
  const publish = (id) => act(`pub-${id}`, () => api.post(`/brochures/${id}/publish`), "Brochure publiée.");
  const unpublish = (id) => act(`unpub-${id}`, () => api.post(`/brochures/${id}/unpublish`), "Brochure retirée — le lien public est coupé.");
  const setDefault = (id) => act(`def-${id}`, () => api.post(`/brochures/${id}/default`), "Brochure définie par défaut pour sa langue.");
  const testSend = (id) => {
    if (!testEmail.includes("@")) { setError("Entrez une adresse courriel de test valide."); return; }
    act(`test-${id}`, () => api.post(`/brochures/${id}/test-send`, { to: testEmail }), `Courriel de test envoyé à ${testEmail}.`);
  };

  return (
    <div data-testid="brochures-page" className="min-h-screen bg-slate-50">
      <PublicNav />
      <div className="bg-brand-navy">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <Link data-testid="brochures-back" to="/prospection"
            className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-semibold mb-3">
            <ArrowLeft className="w-3.5 h-3.5" /> Prospection
          </Link>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-3">
            <FileText className="w-7 h-7 text-brand-turquoise" /> Brochures commerciales
          </h1>
          <p className="text-white/70 text-sm mt-1">
            PDF FR/EN joints aux courriels de prospection par lien sécurisé — uniquement pour les employeurs dont le contact est autorisé (LCAP).
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {error && <div data-testid="brochures-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
        {notice && <div data-testid="brochures-notice" className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">{notice}</div>}

        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="generate-fr" onClick={() => generate("fr")} disabled={busy === "gen-fr"}
            className="flex items-center gap-2 bg-brand-turquoise hover:bg-brand-turquoise/90 disabled:opacity-50 text-white text-sm font-semibold rounded-lg px-4 py-2.5">
            <Upload className="w-4 h-4" /> Générer la version française
          </button>
          <button data-testid="generate-en" onClick={() => generate("en")} disabled={busy === "gen-en"}
            className="flex items-center gap-2 bg-brand-blue hover:bg-brand-blue/90 disabled:opacity-50 text-white text-sm font-semibold rounded-lg px-4 py-2.5">
            <Upload className="w-4 h-4" /> Générer la version anglaise
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <input data-testid="test-email-input" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Adresse contrôlée pour le test" type="email"
              className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 w-64 bg-white" />
          </div>
        </div>

        <div data-testid="brochures-list" className="space-y-3">
          {items.length === 0 && (
            <p data-testid="brochures-empty" className="text-sm text-slate-400 text-center py-10 bg-white rounded-xl border border-slate-200">
              Aucune brochure — générez les versions française et anglaise.
            </p>
          )}
          {items.map((b) => (
            <div key={b.brochure_id} data-testid={`brochure-row-${b.brochure_id}`}
              className="bg-white rounded-xl border border-slate-200 px-5 py-4 flex flex-wrap items-center gap-3">
              <span className={`text-[10px] font-bold uppercase rounded-full px-2.5 py-1 ${b.language === "fr" ? "bg-brand-sky text-brand-navy" : "bg-slate-100 text-slate-600"}`}>
                {b.language === "fr" ? "Français" : "English"}
              </span>
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-semibold text-brand-navy">
                  Version {b.version}
                  {b.is_default && <span data-testid={`brochure-default-${b.brochure_id}`} className="ml-2 text-[10px] font-bold text-amber-600 uppercase">★ Par défaut</span>}
                </p>
                <p className="text-xs text-slate-500">
                  {Math.round((b.size_bytes || 0) / 1024)} Ko · envoyée {b.sent_count || 0}× · téléchargée {b.download_count || 0}×
                  {b.last_error && <span className="text-red-600"> · erreur : {b.last_error}</span>}
                </p>
              </div>
              <span data-testid={`brochure-status-${b.brochure_id}`} className={`text-xs font-semibold rounded-full px-2.5 py-1 ${STATUS_STYLE[b.status] || ""}`}>
                {STATUS_LABEL[b.status] || b.status}
              </span>
              <div className="flex items-center gap-1.5">
                <a data-testid={`brochure-download-${b.brochure_id}`} href={`${API}/api/brochures/${b.brochure_id}/download`}
                  className="flex items-center gap-1 text-xs font-semibold text-brand-navy border border-slate-200 rounded-lg px-3 py-2 hover:border-brand-turquoise">
                  <Download className="w-3.5 h-3.5" /> PDF
                </a>
                {b.status !== "publiee" && (
                  <button data-testid={`brochure-publish-${b.brochure_id}`} onClick={() => publish(b.brochure_id)} disabled={!!busy}
                    className="text-xs font-semibold bg-green-600 text-white rounded-lg px-3 py-2 hover:bg-green-700 disabled:opacity-50">Publier</button>
                )}
                {b.status === "publiee" && !b.is_default && (
                  <button data-testid={`brochure-setdefault-${b.brochure_id}`} onClick={() => setDefault(b.brochure_id)} disabled={!!busy}
                    className="flex items-center gap-1 text-xs font-semibold text-amber-700 border border-amber-200 rounded-lg px-3 py-2 hover:bg-amber-50 disabled:opacity-50">
                    <Star className="w-3.5 h-3.5" /> Défaut
                  </button>
                )}
                {b.status === "publiee" && (
                  <>
                    <button data-testid={`brochure-testsend-${b.brochure_id}`} onClick={() => testSend(b.brochure_id)} disabled={!!busy}
                      className="flex items-center gap-1 text-xs font-semibold text-brand-blue border border-slate-200 rounded-lg px-3 py-2 hover:border-brand-blue disabled:opacity-50">
                      <Send className="w-3.5 h-3.5" /> Test
                    </button>
                    <button data-testid={`brochure-unpublish-${b.brochure_id}`} onClick={() => unpublish(b.brochure_id)} disabled={!!busy}
                      className="flex items-center gap-1 text-xs font-semibold text-slate-500 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50 disabled:opacity-50">
                      <EyeOff className="w-3.5 h-3.5" /> Retirer
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-slate-400">
          Sélection automatique dans les campagnes : brochure publiée par défaut de la langue du destinataire (Québec → français, sinon anglais).
          Chaque envoi enregistre la version dans l'événement de campagne. Si le fichier est indisponible, l'erreur est journalisée et le courriel part sans brochure.
        </p>
      </div>
    </div>
  );
}
